import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {Box, Button, ButtonBase, Menu, MenuItem, Stack, Typography} from 'views/components';
import {AddOutlined, RefreshOutlined} from 'views/icons';

import {formatTokens} from './chatUtils';

const iconButtonSx = {
  'minWidth': '0',
  'borderRadius': '7px',
  'p': '4px',
  'color': '#A8A49E',
  'backgroundColor': 'transparent',
  '&:hover': {backgroundColor: '#F3F1EE', boxShadow: 'none'},
};

const SessionRow = ({session, selected, onSelect}) => (
  <ButtonBase
    onClick={() => onSelect(session.sessionRn)}
    sx={{
      'position': 'relative',
      'display': 'flex',
      'alignItems': 'flex-start',
      'flexDirection': 'column',
      'width': '100%',
      'px': '12px',
      'py': '10px',
      'borderRadius': '8px',
      'textAlign': 'left',
      'backgroundColor': selected ? '#F3F1EE' : 'transparent',
      '&:hover': {backgroundColor: selected ? '#F3F1EE' : '#F8F6F3'},
      '&::before': selected ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '20%',
        bottom: '20%',
        width: '4px',
        borderRadius: '0 2px 2px 0',
        backgroundColor: '#DA7955',
      } : {},
    }}
  >
    <Typography
      sx={{
        fontSize: '13px',
        fontWeight: selected ? 500 : 400,
        color: '#1A1916',
        lineHeight: '130%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      }}
    >
      {session.title || 'Untitled'}
    </Typography>
    <Stack direction='row' alignItems='center' sx={{gap: '6px', mt: '3px'}}>
      <Typography sx={{fontSize: '11px', color: '#A8A49E', lineHeight: '100%'}}>
        {moment(session.updatedAt).format('MMM D, YYYY')}
      </Typography>
      {session.agentTarget === 'EVENT_ROUTER_DISCIPLINE' && (
        <Typography
          sx={{
            fontSize: '9px',
            fontWeight: 500,
            color: '#DA7955',
            lineHeight: '100%',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            px: '5px',
            py: '2px',
            borderRadius: '999px',
            border: '1px solid #F0D5C9',
            backgroundColor: '#FBF3EF',
          }}
        >
          Hudson
        </Typography>
      )}
    </Stack>
    {session.usage && (
      <Typography sx={{fontSize: '10px', color: '#A8A49E', lineHeight: '100%', mt: '4px'}}>
        {formatTokens(session.usage)}
      </Typography>
    )}
  </ButtonBase>
);

SessionRow.propTypes = {
  session: PropTypes.shape({
    sessionRn: PropTypes.string.isRequired,
    title: PropTypes.string,
    updatedAt: PropTypes.string,
    agentTarget: PropTypes.string,
    usage: PropTypes.object,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const ChatSessionList = ({sessions, selectedSessionRn, onSelect, onNewChat, onRefresh}) => {
  const [newChatMenuAnchor, setNewChatMenuAnchor] = React.useState(null);

  const startNewChat = (target) => {
    setNewChatMenuAnchor(null);
    onNewChat(target);
  };

  return (
    <Box sx={{flexShrink: 0, alignSelf: 'stretch', display: 'flex'}}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '280px',
          border: '1px solid',
          borderColor: '#E8E4DE',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          sx={{px: '32px', paddingTop: '24px', paddingBottom: '12px', gap: '8px'}}
        >
          <Stack sx={{gap: '8px'}}>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '17px',
                color: '#1A1916',
                lineHeight: '100%',
                textDecoration: 'underline',
                textDecorationColor: '#DA7955',
                textUnderlineOffset: '5px',
              }}
            >
              Chat
            </Typography>
            <Typography sx={{fontWeight: 400, fontSize: '12px', color: '#A8A49E', lineHeight: '100%', whiteSpace: 'nowrap'}}>
              Conversation history
            </Typography>
          </Stack>
          <Stack direction='row' sx={{gap: '4px'}}>
            <Button
              onClick={(e) => setNewChatMenuAnchor(e.currentTarget)}
              disableElevation
              title='New conversation'
              sx={iconButtonSx}
            >
              <AddOutlined sx={{fontSize: 16}} />
            </Button>
            <Menu
              anchorEl={newChatMenuAnchor}
              open={Boolean(newChatMenuAnchor)}
              onClose={() => setNewChatMenuAnchor(null)}
              disableRestoreFocus
            >
              <MenuItem onClick={() => startNewChat()} sx={{fontSize: '13px', color: '#1A1916'}}>
                Chanel
              </MenuItem>
              <MenuItem onClick={() => startNewChat('eventRouterDiscipline')} sx={{fontSize: '13px', color: '#1A1916'}}>
                Hudson
              </MenuItem>
            </Menu>
            <Button
              onClick={onRefresh}
              disableElevation
              title='Refresh'
              sx={iconButtonSx}
            >
              <RefreshOutlined sx={{fontSize: 16}} />
            </Button>
          </Stack>
        </Stack>

        <Box sx={{borderBottom: '1px solid #E8E4DE'}} />

        <Stack sx={{flex: 1, px: '8px', py: '8px', gap: '2px', overflowY: 'auto'}}>
          {sessions.length === 0 && (
            <Typography sx={{px: '8px', py: '8px', fontSize: '12px', color: '#A8A49E', lineHeight: '150%'}}>
              No conversations yet
            </Typography>
          )}
          {sessions.map((session) => (
            <SessionRow
              key={session.sessionRn}
              session={session}
              selected={session.sessionRn === selectedSessionRn}
              onSelect={onSelect}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

ChatSessionList.propTypes = {
  sessions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedSessionRn: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default ChatSessionList;
